export interface CompoundMultiplierContent {
  title: string;
  subtitle: string;
  tagline: string;
  
  // Core concept explanation
  whatMakesDifferent: {
    title: string;
    traditional: {
      title: string;
      points: string[];
    };
    multiplier: {
      title: string;
      points: string[];
    };
  };
  
  // The 4 pillars
  pillars: {
    title: string;
    items: Array<{
      number: number;
      title: string;
      description: string;
      examples: string[];
    }>;
  };
  
  // Sales phrases
  salesPhrases: {
    opening: string[];
    explanation: string[];
    closing: string[];
  };
  
  // Visual examples
  visualExamples: {
    directComparison: {
      traditional: string;
      multiplier: string;
    };
    avalancheEffect: string[];
  };
  
  // Unique benefits
  uniqueBenefits: Array<{
    title: string;
    description: string;
  }>;
  
  // Common objections and responses
  objections: Array<{
    objection: string;
    response: string;
  }>;
  
  // Call to action
  callToAction: string;
  
  // Positioning
  positioning: string[];
}

export const compoundMultiplierContent: CompoundMultiplierContent = {
  title: "INTERÉS COMPUESTO MULTIPLICADOR™",
  subtitle: "La Nueva Era de Crecimiento Patrimonial",
  tagline: "No solo multiplica dinero, multiplica activos reales productivos",
  
  whatMakesDifferent: {
    title: "¿Qué lo hace diferente del interés compuesto tradicional?",
    traditional: {
      title: "Interés Compuesto Tradicional:",
      points: [
        "Reinvierte dinero que genera más dinero",
        "Crecimiento en una sola dimensión (valor monetario)",
        "Dependiente de tasas de mercado volátiles",
        "Activos intangibles (números en pantalla)"
      ]
    },
    multiplier: {
      title: "Interés Compuesto Multiplicador™:",
      points: [
        "Reinvierte utilidades que adquieren activos reales productivos",
        "Crecimiento en múltiples dimensiones (cantidad + valor + producción)",
        "Doble motor de crecimiento: apreciación + rendimiento operativo",
        "Activos tangibles (árboles que producen frutos reales)"
      ]
    }
  },
  
  pillars: {
    title: "Los 4 Pilares del Interés Compuesto Multiplicador™",
    items: [
      {
        number: 1,
        title: "MULTIPLICACIÓN DE ACTIVOS",
        description: "No solo crece el dinero, crece tu imperio productivo",
        examples: [
          "Año 1: 1 certificado → 1,000 m² productivos",
          "Año 10: 3 certificados → 3,000 m² productivos",
          "Año 20: 8 certificados → 8,000 m² productivos"
        ]
      },
      {
        number: 2,
        title: "MULTIPLICACIÓN DE INGRESOS",
        description: "Cada nuevo certificado es una nueva fuente de ingresos permanente",
        examples: [
          "Tradicional: $100K → $200K (2x en dinero)",
          "Multiplicador: 1 certificado → 8 certificados (8x en activos productivos)",
          "Cada certificado = nueva fuente de ingresos permanente"
        ]
      },
      {
        number: 3,
        title: "MULTIPLICACIÓN DE VALOR",
        description: "Triple motor de crecimiento simultáneo",
        examples: [
          "Apreciación del activo (12% anual primeros 5 años)",
          "Incremento del producto (5% anual en precio del limón)",
          "Efecto red (más certificados = mayor poder de negociación)"
        ]
      },
      {
        number: 4,
        title: "MULTIPLICACIÓN DE IMPACTO",
        description: "Tu impacto positivo crece exponencialmente",
        examples: [
          "Ambiental: Más árboles = más CO₂ capturado",
          "Social: Más certificados = más empleos generados",
          "Económico: Mayor contribución al desarrollo regional"
        ]
      }
    ]
  },
  
  salesPhrases: {
    opening: [
      "¿Has escuchado sobre el Interés Compuesto Multiplicador? Es la evolución natural del interés compuesto tradicional, pero en lugar de solo multiplicar dinero, multiplica activos reales que producen ingresos.",
      "Te voy a mostrar algo que va a cambiar tu perspectiva sobre las inversiones: el Interés Compuesto Multiplicador™, donde no solo crece tu dinero, sino que se multiplican tus fuentes de ingresos."
    ],
    explanation: [
      "Mientras el interés compuesto tradicional te da más dinero, el Interés Compuesto Multiplicador te da más FUENTES de dinero. Es como la diferencia entre tener una gallina que pone huevos de oro, versus tener un gallinero completo.",
      "Imagínate que en lugar de que tu dinero solo crezca, cada año adquieres nuevos activos productivos que trabajan para ti. Eso es el Interés Compuesto Multiplicador™."
    ],
    closing: [
      "Con el Interés Compuesto Multiplicador, no solo estás invirtiendo en tu futuro financiero, estás construyendo un imperio productivo que trabajará para ti las 24 horas del día, los 365 días del año.",
      "El Interés Compuesto Multiplicador no es solo una inversión, es una revolución financiera personal. Mientras otros siguen jugando con números en pantallas, tú estarás construyendo un imperio real que puedes ver, tocar y heredar."
    ]
  },
  
  visualExamples: {
    directComparison: {
      traditional: "INTERÉS COMPUESTO TRADICIONAL:\n$100,000 → $200,000 → $400,000 → $800,000\n(Solo números que crecen)",
      multiplier: "INTERÉS COMPUESTO MULTIPLICADOR™:\n1 certificado → 2 certificados → 4 certificados → 8 certificados\n(Activos reales que se multiplican Y producen)"
    },
    avalancheEffect: [
      "Año 5: Tu primer certificado empieza a producir",
      "Año 6: Las utilidades compran parte del certificado 2",
      "Año 8: Certificado 2 completo y produciendo",
      "Año 9: Certificados 1 y 2 compran certificado 3",
      "Año 11: Certificados 1, 2 y 3 compran certificados 4 y 5",
      "Año 20: ¡Tienes un ejército de certificados trabajando para ti!"
    ]
  },
  
  uniqueBenefits: [
    {
      title: "DIVERSIFICACIÓN AUTOMÁTICA",
      description: "Cada nuevo certificado diversifica automáticamente tu riesgo en diferentes lotes de producción"
    },
    {
      title: "ESCALABILIDAD EXPONENCIAL",
      description: "No hay límite en cuántos certificados puedes llegar a tener - el sistema se auto-alimenta"
    },
    {
      title: "PROTECCIÓN INFLACIONARIA",
      description: "Mientras la inflación devora el dinero tradicional, tus activos reales se aprecian con ella"
    },
    {
      title: "HERENCIA TANGIBLE",
      description: "Dejas a tus hijos un patrimonio real y productivo, no solo números en una cuenta"
    }
  ],
  
  objections: [
    {
      objection: "¿No es lo mismo que cualquier inversión que reinvierte?",
      response: "No. Las inversiones tradicionales reinvierten en el mismo activo. Nosotros multiplicamos la CANTIDAD de activos productivos. Es como la diferencia entre hacer más grande una gallina versus tener más gallinas."
    },
    {
      objection: "¿Qué pasa si quiero liquidez?",
      response: "El Interés Compuesto Multiplicador te permite configurar retiros parciales. Puedes disfrutar ingresos mensuales mientras sigues multiplicando tu patrimonio productivo."
    },
    {
      objection: "¿Es muy riesgoso?",
      response: "Al contrario, es menos riesgoso porque diversificas automáticamente. En lugar de depender de un solo activo grande, tienes múltiples activos pequeños que se respaldan mutuamente."
    }
  ],
  
  callToAction: "El Interés Compuesto Multiplicador no es solo una inversión, es una revolución financiera personal. Mientras otros siguen jugando con números en pantallas, tú estarás construyendo un imperio real que puedes ver, tocar y heredar. ¿Estás listo para ser pionero de tu propia revolución financiera?",
  
  positioning: [
    "Una innovación financiera exclusiva de Cosecha Capital",
    "La evolución natural del interés compuesto tradicional",
    "Una ventaja competitiva que solo tus clientes tendrán acceso",
    "El futuro de las inversiones respaldadas por activos reales"
  ]
};

// Re-export utility functions
export { getCompoundMultiplierExplanation, generateAvalancheTimeline } from '../utils/compoundInterestUtils';